/** Fit the fixed design canvas to whatever web view the POS actually gives us.
 *
 * Every screen is laid out against the Figma canvas of 1194×834. The LSK web view is NOT that
 * size: on an 11" iPad it measures roughly 1075×727 (the extension runs in a modal inset from the
 * screen edges), and it shifts again with device model, split view and orientation. Laid out at
 * design pixels inside a smaller box, everything reads oversized and the bottom row of controls
 * falls off the screen.
 *
 * So the whole UI is scaled by the smaller of the two ratios, which guarantees the design fits
 * without ever cropping it. Scaling rather than reflowing is deliberate: the design is a fixed
 * till layout, the proportions are the approved thing, and a cashier's muscle memory depends on
 * controls staying where they are.
 *
 * Applied with CSS `zoom` (see `index.css`), not `transform: scale()`. A transform does not affect
 * layout, so the page would keep its unscaled scroll box; it also does not reach elements in the
 * top layer, which would leave every `<dialog>` rendering at full size over a shrunken app.
 */
const DESIGN_WIDTH = 1194
const DESIGN_HEIGHT = 834

/** Never scale up past the design, and never shrink so far the text stops being readable across a
 * counter — below this a device is too small for this layout and reflowing would be the answer. */
const MIN_SCALE = 0.6

export function computeUiScale(width: number, height: number): number {
  if (!(width > 0) || !(height > 0)) return 1
  const fit = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT)
  return Math.max(MIN_SCALE, Math.min(1, fit))
}

/** Keep `--ui-scale` in sync with the viewport. Returns a cleanup function. */
export function installUiScale(): () => void {
  if (typeof window === 'undefined') return () => {}

  const apply = () => {
    const scale = computeUiScale(window.innerWidth, window.innerHeight)
    document.documentElement.style.setProperty('--ui-scale', String(scale))
  }

  apply()
  window.addEventListener('resize', apply)
  // iPadOS fires orientationchange before the new innerWidth/innerHeight are settled, so re-read
  // on the resize that follows it as well — `apply` is idempotent, so running twice is free.
  window.addEventListener('orientationchange', apply)
  return () => {
    window.removeEventListener('resize', apply)
    window.removeEventListener('orientationchange', apply)
  }
}
